<script setup>
import { PLACEHOLDER_PHOTO_URL } from 'configs/profile-photo';
import { computed, onMounted, ref, watch } from 'vue';

const $props = defineProps({
    src: {
        type: String,
        default: null
    },
    alt: {
        type: String,
        default: 'User profile photo'
    },
    size: {
        type: String,
        default: null
    },
    bordered: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const photoElement = ref(null);
const hasLoadError = ref(false);

// A photo that cannot be loaded falls back to the placeholder, rather than leaving its alt text in the avatar at the
// avatar's own font size.
const photoUrl = computed(() => {
    return $props.src && !hasLoadError.value ? $props.src : PLACEHOLDER_PHOTO_URL;
});

// A server-rendered photo starts loading before hydration attaches the error listener, so one that has already failed
// by then is caught from its own state instead.
onMounted(() => {
    if (photoElement.value?.complete && photoElement.value.naturalWidth === 0) {
        handleLoadError();
    }
});

watch(() => $props.src, () => {
    hasLoadError.value = false;
});

function handleLoadError() {
    if (photoUrl.value !== PLACEHOLDER_PHOTO_URL) {
        hasLoadError.value = true;
    }
}
</script>

<template>
    <q-avatar :class="['user-avatar user-profile-photo', { 'bordered': bordered }]" :size="size ?? undefined">
        <q-skeleton v-if="loading" type="rect"/>
        <img v-else ref="photoElement" :alt="alt" :src="photoUrl" @error="handleLoadError"/>
        <slot></slot>
    </q-avatar>
</template>

<style lang="scss" scoped>
.user-profile-photo {
    align-items: center;
    aspect-ratio: 1/1;
    border-radius: $generic-border-radius;
    display: flex;
    justify-content: center;
    position: relative;

    .q-skeleton {
        height: calc(100% - 6px);
        margin: 2px;
        width: calc(100% - 4px);
    }

    img {
        display: block;
        // If even the placeholder cannot load, the alt text stays available to a screen reader without filling the
        // avatar at its own font size.
        font-size: 0;
        height: auto;
        max-height: 100%;
        max-width: 100%;
        object-fit: contain; /* Shows the entire image within the container */
        width: auto;
    }

    &.bordered {
        border: 1px solid $separator-color;

        .body--dark & {
            border-color: $separator-dark-color;
        }
    }
}
</style>
